import React, { useState, useCallback, useId } from "react";
import { ChevronDown } from "lucide-react";

/**
 * MitelAccordion — sezioni di livello 2 (approfondimenti).
 *
 * Accessibilità:
 *  - Trigger è <button> con aria-expanded/aria-controls.
 *  - Contenuto ha id + role="region" e aria-labelledby verso il trigger.
 *  - Chevron ruota, focus visibile via ring.
 *  - Testo/heading semanticamente corretti (heading passato dall'esterno).
 */
export default function MitelAccordion({
  eyebrow,
  title,
  children,
  defaultOpen = false,
  testId,
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const autoId = useId();
  const panelId = `mitel-acc-panel-${autoId}`;
  const buttonId = `mitel-acc-btn-${autoId}`;

  const toggle = useCallback(() => setOpen((v) => !v), []);

  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
    >
      <h3 className="m-0">
        <button
          type="button"
          id={buttonId}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={toggle}
          data-testid={testId ? `${testId}-toggle` : undefined}
          className="w-full flex items-center justify-between gap-4 text-left px-4 sm:px-5 py-4 hover:bg-white/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50 transition"
        >
          <span className="min-w-0">
            {eyebrow && (
              <span className="block text-[10px] tracking-[0.32em] uppercase text-emerald-300/80 mb-1">
                {eyebrow}
              </span>
            )}
            <span className="block text-[15px] sm:text-base font-light text-white leading-snug">
              {title}
            </span>
          </span>
          <ChevronDown
            className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${
              open ? "rotate-180 text-emerald-300" : ""
            }`}
            aria-hidden="true"
          />
        </button>
      </h3>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          className="px-4 sm:px-5 pb-5 pt-1 text-[13px] sm:text-sm font-light text-white/70 leading-relaxed space-y-3"
        >
          {children}
        </div>
      )}
    </div>
  );
}
